import React, { useEffect, useMemo } from 'react'
import { Button, Stack, useMediaQuery, useTheme } from '@mui/material'
import moment, { Moment } from 'moment'
import { sendMessageToReactNative } from '@e3dc-react/shell/Libs/react-native'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { sanitizeString } from '@e3dc-react/shell/Libs/strings'
import { DatePicker } from '@e3dc-react/shell/Elements/DatePicker'
import { LegendAI360, AI360LineChart, ModeDetailsModal, DetailsPageSkeleton, DetailsPageHeader } from '../components/details-page'
import { AI360ChartData, AI360Mode } from '../types'
import { AI360ModesTimeline, AI360EmsBatteryLegend, AI360SgReadyLegend, RotatePhoneToLandscapeMessage } from '../components'
import { useCheckOrientation } from 'src/Libs/chart-kit'
import { addDays, startOfDay } from '@e3dc-react/shell/Libs/date'

interface DetailsPageProps {
  chartData: AI360ChartData[]
  dateSelected: Date
  from: Date
  to: Date
  setDateSelected: (date: Date) => void
  isModeModalOpen: boolean
  setIsModeModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const DetailsPage: React.FC<DetailsPageProps> = ({ chartData, dateSelected, from, to, setDateSelected, isModeModalOpen, setIsModeModalOpen }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const isUsedInPortrait = useCheckOrientation()

  const hasSgReadyData = useMemo(() => chartData?.some(seg => seg.emsSgReadyMode !== null), [chartData])

  const nextDay = startOfDay(addDays(new Date(), 1))

  useEffect(() => {
    sendMessageToReactNative({ type: 'use-landscape-orientation' })

    return () => {
      sendMessageToReactNative({ type: 'use-portrait-orientation' })
    }
  }, [])

  const handleToggleModalOpen = (): void => {
    setIsModeModalOpen(prev => !prev)
  }

  const hasNoChartData = chartData.every(
    data =>
      data.powerPrice === null &&
      data.solarProduction === null &&
      data.solarForecast === null &&
      data.feedInPrice === null &&
      data.emsBatteryMode === null &&
      data.emsSgReadyMode === null
  )

  if (isUsedInPortrait) {
    return <RotatePhoneToLandscapeMessage />
  }

  /* Show blank state if there's no data to display in the chart */
  if (hasNoChartData) {
    return <DetailsPageSkeleton />
  }

  return (
    <>
      {/* Line Chart Section */}
      <Stack flexDirection="row" mt={0} mb={2} flex={1}>
        <Stack justifyContent="space-between" sx={{ maxWidth: upMd ? '300px' : '30vw', minWidth: upMd ? '300px' : '30vw', mr: 2 }}>
          <Stack gap={2}>
            <DetailsPageHeader />
            <DatePicker
              value={moment(dateSelected)}
              handleDateChange={(date: Moment | null) => {
                if (date) setDateSelected(date.toDate())
              }}
              inputFormat={sanitizeString(t('common.format.date'))}
              /* max date user can select from date picker: 1 day from today */
              maxFrom={moment(nextDay)}
              maxDate={moment(nextDay)}
            />
            <LegendAI360 />
          </Stack>
          <Button sx={{ ml: 2, mr: upMd ? 4 : 1 }} variant="outlined" onClick={handleToggleModalOpen}>
            {t('ai360.details-page.explanation-of-mode-modal')}
          </Button>
        </Stack>
        <Stack height={'100%'} minHeight={0} width={'100%'}>
          <AI360LineChart chartData={chartData} from={from} to={to} syncId="ai360-detail-chart" />
        </Stack>
      </Stack>

      {/* EMS Battery Section */}
      <Stack flexDirection="row" alignItems="center" flexShrink={0} gap={2}>
        <AI360EmsBatteryLegend />
        <AI360ModesTimeline chartData={chartData} syncId="ai360-detail-chart" mode={AI360Mode.EmsBatteryMode} />
      </Stack>

      {/* SG Ready Section */}
      {hasSgReadyData && (
        <Stack flexDirection="row" alignItems="center" flexShrink={0} gap={2}>
          <AI360SgReadyLegend />
          <AI360ModesTimeline chartData={chartData} syncId="ai360-detail-chart" mode={AI360Mode.SgReadyMode} />
        </Stack>
      )}

      {/* Mode Details Modal */}
      {isModeModalOpen && <ModeDetailsModal open={isModeModalOpen} onClose={handleToggleModalOpen} hasSgReadyData={hasSgReadyData} />}
    </>
  )
}

export default DetailsPage
